import { useEffect, useState } from 'react';
import { IdentityService } from '../api/client.js';
import type { Principal } from '../api/client.js';
import { hasAdminRole } from './roles.js';

// Module-scope cache: all hook callers (AppLayout, Audit page, etc.) share the
// same in-flight (or already-settled) promise so there is only one /v1/me
// request per page load.
let mePromise: Promise<Principal> | undefined;

function getMeOnce(): Promise<Principal> {
  if (mePromise === undefined) {
    mePromise = IdentityService.getMe();
  }
  return mePromise;
}

/**
 * Returns:
 *   - `undefined`  while the /v1/me request is in-flight
 *   - `true`       when the principal holds the `tps-admin` role
 *   - `false`      when roles absent, or if the request fails (e.g. 401/403)
 *
 * The getMe promise is memoized for the whole page session, so a failed /v1/me
 * is NOT retried until a hard reload. This is intentional — it collapses all
 * callers onto a single request; do not add retry logic here.
 */
export function useIsAdmin(): boolean | undefined {
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    getMeOnce()
      .then((principal) => {
        if (!cancelled) {
          setIsAdmin(hasAdminRole(principal));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsAdmin(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return isAdmin;
}
